import { h, clear } from '../ui/dom.js';
import {
  card,
  button,
  field,
  input,
  select,
  textarea,
  table,
  emptyState,
  openModal,
  confirmDialog,
  toast,
  badge,
} from '../ui/components.js';
import { api, ApiError } from '../api.js';
import { can } from '../store.js';
import type { Page, PageContext } from '../page.js';
import { ENCOUNTER_KIND_LABELS, type EncounterKind, type Template } from '../types.js';

interface DraftMinistration {
  title: string;
  suggestedDurationMin: number;
  script: string;
}

export async function templatesPage(context: PageContext): Promise<Page> {
  const { items } = await api.get<{ items: Template[] }>('/api/templates');

  const rows = items.map((template) => [
    h(
      'div',
      { class: 'stack stack--sm' },
      h('div', { style: { fontWeight: '600' }, text: template.name }),
      h('div', { class: 'muted small', text: template.description ?? 'Sem descrição' }),
    ),
    h('div', null, badge(ENCOUNTER_KIND_LABELS[template.kind], 'accent')),
    h('div', { text: String(template.ministrationsCount ?? 0) }),
    h(
      'div',
      null,
      template.isOfficial ? badge('Oficial', 'success') : badge('Rascunho', 'neutral'),
    ),
    h(
      'div',
      { class: 'table__actions' },
      button('Abrir', {
        size: 'sm',
        variant: 'ghost',
        onClick: () => context.navigate(`/modelos/${template.id}`),
      }),
    ),
  ]);

  const actions = can.manageTemplates()
    ? [button('Novo modelo', { onClick: () => openTemplateModal(() => void reload(context)) })]
    : [];

  if (context.query.get('novo') === '1' && can.manageTemplates()) {
    openTemplateModal(() => void reload(context));
  }

  return {
    title: 'Modelos Mestre',
    subtitle:
      'Estrutura oficial de cada tipo de encontro: ministrações, tempos sugeridos e materiais-base.',
    actions,
    body: card({
      title: 'Modelos cadastrados',
      hint: can.manageTemplates()
        ? 'Como Administrador Geral ou Pastor, você pode criar, editar e excluir modelos.'
        : 'Seu perfil tem acesso somente leitura aos modelos.',
      body:
        items.length > 0
          ? table(['Modelo', 'Tipo', 'Ministrações', 'Situação', ''], rows)
          : emptyState('Nenhum modelo cadastrado ainda.', '⧉'),
    }),
  };
}

async function reload(context: PageContext): Promise<void> {
  context.navigate('/modelos');
  await Promise.resolve();
  window.location.reload();
}

/** Formulario de criacao de modelo com construtor de ministrações. */
function openTemplateModal(onCreated: () => void): void {
  const nameInput = input({ placeholder: 'Ex.: Encontro de Homens 2026' });
  const kindSelect = select(
    (Object.keys(ENCOUNTER_KIND_LABELS) as EncounterKind[]).map((kind) => ({
      value: kind,
      label: ENCOUNTER_KIND_LABELS[kind],
    })),
  );
  const descriptionInput = input({ placeholder: 'Breve descrição do modelo' });

  const drafts: DraftMinistration[] = [
    { title: 'Abertura', suggestedDurationMin: 20, script: '' },
    { title: 'Palavra Central', suggestedDurationMin: 45, script: '' },
    { title: 'Encerramento', suggestedDurationMin: 15, script: '' },
  ];

  const draftsContainer = h('div', { class: 'stack' });
  const errorSlot = h('div', { class: 'field__error' });

  const renderDrafts = (): void => {
    clear(draftsContainer);
    drafts.forEach((draft, index) => {
      const titleInput = input({
        value: draft.title,
        placeholder: 'Título da ministração',
        oninput: (event: Event) => {
          draft.title = (event.target as HTMLInputElement).value;
        },
      });
      const durationInput = input({
        type: 'number',
        value: String(draft.suggestedDurationMin),
        attrs: { min: '5', max: '600' },
        oninput: (event: Event) => {
          draft.suggestedDurationMin = Number((event.target as HTMLInputElement).value) || 0;
        },
      });
      const scriptInput = textarea({
        value: draft.script,
        placeholder: 'Roteiro base (opcional)',
        oninput: (event: Event) => {
          draft.script = (event.target as HTMLTextAreaElement).value;
        },
      });

      draftsContainer.append(
        h(
          'div',
          { class: 'card', style: { padding: '12px' } },
          h(
            'div',
            { class: 'row row--between', style: { marginBottom: '8px' } },
            h('div', { class: 'badge badge--neutral', text: `Ministração ${index + 1}` }),
            button('Remover', {
              size: 'sm',
              variant: 'ghost',
              disabled: drafts.length <= 1,
              onClick: () => {
                drafts.splice(index, 1);
                renderDrafts();
              },
            }),
          ),
          h(
            'div',
            { class: 'form-grid' },
            field({ label: 'Título', control: titleInput, required: true }),
            field({ label: 'Duração sugerida (min)', control: durationInput, required: true }),
          ),
          h('div', { style: { marginTop: '12px' } }, field({ label: 'Roteiro base', control: scriptInput })),
        ),
      );
    });
  };

  renderDrafts();

  const submit = async (): Promise<void> => {
    clear(errorSlot);
    try {
      await api.post('/api/templates', {
        name: nameInput.value.trim(),
        kind: kindSelect.value,
        description: descriptionInput.value.trim() || null,
        isOfficial: true,
        ministrations: drafts
          .filter((draft) => draft.title.trim().length >= 2)
          .map((draft) => ({
            title: draft.title.trim(),
            suggestedDurationMin: draft.suggestedDurationMin || 30,
            script: draft.script,
          })),
      });
      modal.close();
      toast.success('Modelo criado', 'O modelo já pode ser usado para gerar novas edições.');
      onCreated();
    } catch (error) {
      errorSlot.textContent = error instanceof ApiError ? error.message : 'Falha ao criar o modelo.';
    }
  };

  const modal = openModal({
    title: 'Novo modelo mestre',
    wide: true,
    body: [
      h(
        'div',
        { class: 'form-grid' },
        field({ label: 'Nome do modelo', control: nameInput, required: true }),
        field({ label: 'Tipo de encontro', control: kindSelect, required: true }),
        field({ label: 'Descrição', control: descriptionInput }),
      ),
      h('hr', { class: 'separator' }),
      h(
        'div',
        { class: 'row row--between' },
        h('div', { class: 'card__title', text: 'Ministrações do modelo' }),
        button('+ Adicionar ministração', {
          size: 'sm',
          variant: 'ghost',
          onClick: () => {
            drafts.push({ title: '', suggestedDurationMin: 30, script: '' });
            renderDrafts();
          },
        }),
      ),
      draftsContainer,
      errorSlot,
    ],
    footer: [
      button('Cancelar', { variant: 'ghost', onClick: () => modal.close() }),
      button('Criar modelo', { onClick: () => void submit() }),
    ],
  });
}

/** Reexportado para uso em telas que precisam confirmar exclusao de modelo. */
export function confirmTemplateRemoval(name: string, onConfirm: () => void): void {
  confirmDialog({
    title: 'Excluir modelo',
    message: `Tem certeza que deseja excluir o modelo "${name}"? As ministrações vinculadas serão removidas.`,
    confirmLabel: 'Excluir',
    danger: true,
    onConfirm,
  });
}
